import React, { useEffect, useState, useReducer, useRef } from "react";
import { useAtom } from "jotai/index";
import {
  outputTextAtom,
  isThinkingAtom,
  messagesAtom,
  isProcessingAtom,
  isOutputTextAtom,
  standbyModeAtom,
  languageAtom,
} from "~/Atom";
import { talkingModeAtom } from "~/AtomWithDynamoDB";
import type { Message } from "shared/Schemas";
import "~/css/TextBox.css";
import { useTranslation } from "react-i18next";
import { responseTags } from "~/Commons";

const OutputText: React.FC = () => {
  const [isProcessing] = useAtom(isProcessingAtom);
  const [outputText, setOutputText] = useAtom(outputTextAtom);
  const [isThinking] = useAtom(isThinkingAtom);
  const [isOutputText] = useAtom(isOutputTextAtom);
  const [messages, setMessages] = useAtom(messagesAtom);
  const [, setIsVisible] = useState(false);
  const [isReservation, setIsReservation] = useState(false);
  const fadeOutTimer = useRef<NodeJS.Timeout | null>(null);
  const [, setTextSpeed] = useState(60); // ms
  const [, setIsChangingSize] = useState(false);
  const [standby] = useAtom(standbyModeAtom);
  const [language] = useAtom(languageAtom);
  const [talkingMode] = useAtom(talkingModeAtom); // 0:通常, 1:要約のみ発話

  type Action =
    | { type: "UPDATE_TEXT"; text: string }
    | { type: "APPEND_TEXT"; text: string }
    | { type: "CLEAR_TEXT" }
    | { type: "CLEAR_CHAR" }
    | { type: "APPEND_LOADING" }; // 追加
  const { t } = useTranslation();

  const textReducer = (state: string, action: Action) => {
    switch (action.type) {
      case "UPDATE_TEXT":
        return action.text;
      case "APPEND_TEXT":
        return state + action.text;
      case "CLEAR_TEXT":
        return "";
      case "CLEAR_CHAR":
        return state.slice(0, -1); // 最後の文字を削除して新しい文字列を返す
      case "APPEND_LOADING": // 追加
        return state + "■";
      default:
        throw new Error();
    }
  };
  const [displayText, dispatchText] = useReducer(textReducer, "");
  useEffect(() => {
    if (language === "ja-JP") {
      setTextSpeed(60);
    } else {
      setTextSpeed(30);
    }
  }, [language]);

  // 発話中のテキストを表示するためのuseEffect 念の為保存
  // useEffect(() => {
  //   if(talkingMode === 0) {
  //     setIsOutputText(false);
  //     // setIsThinking(false);
  //     return;
  //   }
  //   // console.warn("outputText:", outputText);
  //   if (outputText !== "") {
  //     setIsOutputText(true);
  //     let displayInterval: NodeJS.Timeout;
  //     let index = 0;
  //     if (displayText === t("thinking")) {
  //       dispatchText({ type: "CLEAR_TEXT" });
  //     } else {
  //       dispatchText({ type: "APPEND_TEXT", text: "\n" });
  //     }
  //
  //     const displayNextChar = () => {
  //       dispatchText({ type: "APPEND_LOADING" }); // 追加
  //       setTimeout(() => { // 追加
  //         dispatchText({ type: "CLEAR_CHAR" }); // 追加
  //         dispatchText({ type: "APPEND_TEXT", text: outputText[index] });
  //         index++;
  //         if (index < outputText.length) {
  //           displayInterval = setTimeout(displayNextChar, textSpeed);
  //         } else {
  //           setIsOutputText(false);
  //           clearInterval(displayInterval);
  //         }
  //       }, textSpeed); // 追加
  //     };
  //     displayNextChar();
  //     return () => clearInterval(displayInterval);
  //   }
  //   return undefined;
  // }, [outputText]);

  useEffect(() => {
    const updateMessages = async () => {
      // isThinkingで発話中判定
      if (isOutputText) return;
      if (talkingMode === 1 && isThinking) return;
      if (outputText === "") return;

      setIsChangingSize(false);
      // messagesの最後に追加した要素にassistantのメッセージを追加
      const tmpMessages: Message[] = [...messages];

      // 最新メッセージと同じmessageIdかチェック
      const lastMessage = tmpMessages[tmpMessages.length - 1];
      if (
        lastMessage &&
        lastMessage.messageId === "" &&
        !outputText.startsWith(responseTags.imgTag)
      ) {
        // 同じmessageIdまたは前のmessageIdが空欄の場合は改行して既存メッセージに追記
        lastMessage.content += `\n\n${outputText}`;
      } else {
        // 違うIDまたは初回の場合は新規メッセージを追加
        tmpMessages.push({ role: "assistant", content: outputText, messageId: "" });
      }
      setOutputText("");
      /** ここでまとめて処理終了を通知 **/
      // if (queueArray.length === 0 && summaryQueueArray.length === 0) {
      //   // setIsThinking(false);
      //   setIsProcessing(false);
      // }
      setMessages(tmpMessages); // Reactの再レンダリングをトリガーするためsetMessagesを実行
    };

    updateMessages();
  }, [isThinking, isOutputText, outputText]);

  // テキストボックスの表示切り替え
  const handleDisplayText = () => {
    if (isReservation) {
      setIsVisible(false);
      setIsReservation(false);
    }
  };
  useEffect(() => {
    if (isReservation) {
      fadeOutTimer.current = setTimeout(() => handleDisplayText(), 3000);
    } else {
      clearTimeout(fadeOutTimer.current!);
    }
  }, [isReservation]);
  useEffect(() => {
    if (isOutputText) {
      setIsVisible(true);
      setIsReservation(false);
    }
    if (!isProcessing) {
      setIsReservation(true);
    }
  }, [isOutputText, isProcessing]);

  useEffect(() => {
    if (isProcessing === true) {
      if (!standby) {
        dispatchText({ type: "UPDATE_TEXT", text: t("thinking") });
      } else {
        dispatchText({ type: "UPDATE_TEXT", text: "" });
      }
    }
  }, [isProcessing]);

  useEffect(() => {
    const textarea = document.getElementById("dynamic-textarea");
    if (textarea) {
      // setIsChangingSize(true);
      textarea.style.height = "auto"; // 初期化
      textarea.style.height = `${textarea.scrollHeight}px`; // 高さを設定
      // if(`${textarea.scrollHeight}px`===textarea.style.height)return;
      // textarea.style.height = `${textarea.clientHeight}px`; // 高さをセット

      // const targetHeight = textarea.scrollHeight; // 最終的な高さ
      // let currentHeight = parseInt(textarea.style.height) // 現在の高さ
      // // let currentHeight = textarea.clientHeight // 現在の高さ
      // console.log("targetHeight:", targetHeight)
      // console.log("currentHeight:", currentHeight)
      // const step = 1; // 1pxずつ増やす
      // const interval = setInterval(() => {
      //   if(currentHeight > targetHeight) {
      //   currentHeight -= 1;
      //   }else if(currentHeight < targetHeight){
      //   currentHeight += 1;
      //   }
      //   textarea.style.height = `${currentHeight}px`;
      //   if (currentHeight === targetHeight) {
      //     textarea.style.height = `${targetHeight}px`; // アニメーション終了後に高さを自動に戻す

      //     clearInterval(interval);
      //   }
      // }, step);
    }
  }, [displayText]);

  return (
    <>
      {/* テキスト表示欄を非表示に設定 - 要約機能のため */}
      {/*<textarea*/}
      {/*  id="dynamic-textarea"*/}
      {/*  value={displayText}*/}
      {/*  placeholder=""*/}
      {/*  readOnly={true}*/}
      {/*  className={`ai-textbox ${isVisible ? "fade-in" : "fade-out"}`}*/}
      {/*  // talkingMode==1のとき、キャラクター下のテキスト表示を無効化*/}

      {/*  style={{*/}
      {/*    display: talkingMode === 0 ? "block" : "none",*/}
      {/*  }}*/}

      {/*/>*/}
    </>
  );
};

export default OutputText;
